/*
 * Persistent theme-music controls and playback engines for the application shell.
 * Used with route ThemeSlot components so music can continue while navigation changes.
 * Retargets the same player between the inline slot and dock instead of restarting playback.
 * Example: registerComponents(registry);
 *          <ThemeDock context={context}/>
 */

import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { Icon } from '../../runtime/components/presentation.jsx';
import { registerMediaPlayer, MediaPlayer } from '../media/media-player.jsx';
import { repeat } from 'lit-html/directives/repeat.js';

export class ThemeDock extends UIElement {
  static properties = { playback: { state: true } };

  constructor() {
    super();
    this.playback = { visible: false, records: new Map() };
    this.captureDock = (node) => (this.dock = node);
    this.captureSlot = (node) => (this.slotRoot = node);
    this.captureEngine = (node) => (this.engine = node);
  }

  updated(changed) {
    super.updated(changed);
    if (!this.connection || !this.context) return;
    const theme = this.context.services.theme;
    if (this.attachedTo !== this.connection) {
      this.attachedTo = this.connection;
      const publish = this.connection.guard((state) => (this.playback = state));
      publish(theme.getState());
      this.connection.own(theme.subscribe(publish));
      this.connection.own(
        theme.attachDock({
          host: this.dock,
          slot: this.slotRoot,
          register: this.registerDestination.bind(this)
        })
      );
      this.connection.own(this.registerDestination('theme-player-dock', this.slotRoot));
      this.connection.own(() =>
        this.context.document.body.classList.remove('has-theme-player-dock')
      );
    }
    this.context.document.body.classList.toggle('has-theme-player-dock', this.playback.visible);
    theme.measureDock();
  }

  dismiss() {
    return this.context.services.theme.dismissDockedThemePlayback();
  }

  renderPlayer(record) {
    const player = (
      <MediaPlayer
        ref={record.attached}
        source={record.source}
        lsKey={record.lsKey}
        autoplay={record.autoplay}
        onPlaybackChange={record.onPlaybackChange}
        engineDestination={this.engine}/>
    );
    return this.portal(record.key, record.destination, player);
  }

  render() {
    return (
      <>
        <aside ref={this.captureDock} id="theme-player-dock" class={`theme-player-dock${this.playback.visible ? '' : ' hidden'}`} aria-label="Theme music">
          <div ref={this.captureSlot} id="theme-player-dock-slot" class="theme-player-dock-slot"/>
          <button id="theme-player-dismiss" type="button" class="theme-player-dock-dismiss" aria-label="Stop and close theme music" title="Stop and close" onClick={this.dismiss}>
            <Icon name="close" classes="icon-sm"/>
          </button>
        </aside>
        <div
          ref={this.captureEngine}
          id="theme-player-engine"
          class="theme-player-engine"
          aria-hidden="true"/>
        {repeat(
          this.playback.records.values(),
          (record) => record.key,
          (record) => this.renderPlayer(record)
        )}
      </>
    );
  }
}

export function registerComponents(registry) {
  registerMediaPlayer(registry);
  defineUIElements(registry, { 'tbc-theme-dock': ThemeDock });
}
